/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuesax Admin - VueJS Dashboard Admin Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from "vue";
import Router from "vue-router";
import store from "@store/index";

Vue.use(Router);

import OpcoesContrato from "@/views/pages/Contratos/components/OpcoesContrato.vue";
import BotaoCadastrarEntidade from "@/views/pages/Entidades/components/BotaoCadastrarEntidade.vue";
import BotaoCadastrarParceiro from "@/views/pages/Parceiros/components/BotaoCadastrarParceiro.vue";
import BotaoCadastrarUsuario from "@/views/pages/Usuarios/components/BotaoCadastrarUsuario.vue";

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: "/",
      component: () => import("./layouts/main/Main.vue"),
      children: [
        {
          path: "",
          beforeEnter(to, from, next) {
            return next("/contratos");
          },
          meta: {
            requiresAuth: true
          }
        },
        {
          path: "contratos/:status?",
          name: "contratos",
          component: () => import("./views/pages/Contratos/Index.vue"),
          meta: {
            requiresAuth: true,
            breadcrumb: [
              { title: "Home" },
              { title: "Contratos", active: true }
            ],
            pageTitle: "Contratos"
          }
        },
        {
          path: "/contrato/cadastrar",
          name: "cadastrar-contrato",
          component: () =>
            import("./views/pages/Contratos/CadastrarContrato.vue"),
          meta: {
            requiresAuth: true,
            breadcrumb: [
              { title: "Home", url: "/contratos" },
              { title: "Contratos", url: "/contratos" },
              { title: "Cadastrar Contrato", active: true }
            ],
            pageTitle: "Cadastrar Contrato"
          }
        },
        {
          path: "/contrato/editar/:id",
          name: "gerenciar-contrato",
          component: () => import("./views/pages/Contratos/EditarContrato.vue"),
          meta: {
            requiresAuth: true,
            breadcrumb: [
              { title: "Home", url: "/contratos" },
              { title: "Contratos", url: "/contratos" },
              { title: "Editar Contrato", active: true }
            ],
            pageTitle: "Editar Contrato",
            actions: [OpcoesContrato]
          }
        },
        {
          path: "/contrato/visualizar/:id",
          name: "visualizar-contrato",
          component: () =>
            import("./views/pages/Contratos/VisualizarContrato.vue"),
          meta: {
            requiresAuth: true,
            breadcrumb: [
              { title: "Home", url: "/contratos" },
              { title: "Contratos", url: "/contratos" },
              { title: "Visualizar Contrato", active: true }
            ],
            pageTitle: "Visualizar Contrato"
          }
        },
        {
          path: "/cobrancas",
          name: "cobrancas",
          meta: {
            requiresAuth: true,
            breadcrumb: [
              { title: "Home", url: "/cobrancas" },
              { title: "Clientes", url: "/cobrancas" },
            ],
            pageTitle: "Cobranças"
          },
          component: () => import("./views/pages/Cobrancas/Index.vue")
        },
        {
          path: "/clientes",
          name: "clientes",
          meta: {
            requiresAuth: true,
            breadcrumb: [
              { title: "Home", url: "/clientes" },
              { title: "Clientes", url: "/clientes" },
              { title: "Visualizar Cliente", active: true }
            ],
            pageTitle: "Clientes"
          },
          component: () => import("./views/pages/Clientes/Index.vue")
        },
        {
          path: "/clientes/visualizar/:id",
          name: "visualizar-cliente",
          component: () =>
            import("./views/pages/Clientes/components/VisualizarClientes.vue"),
          meta: {
            requiresAuth: true,
            breadcrumb: [
              { title: "Home", url: "/clientes" },
              { title: "Cliente", url: "/clientes" },
              { title: "Visualizar Cliente", active: true }
            ],
            pageTitle: "Visualizar Cliente"
          }
        },
        {
          path: "/usuarios",
          name: "usuarios",
          meta: {
            requiresAuth: true,
            breadcrumb: [
              { title: "Home", url: "/usuarios" },
              { title: "Usuários", url: "/usuarios" },
              { title: "Editar Usuário", active: true }
            ],
            actions: [BotaoCadastrarUsuario]
          },
          component: () => import("./views/pages/Usuarios/Index.vue")
        },
        {
          path: "/usuarios/editar/:id",
          name: "gerenciar-usuario",
          component: () => import("./views/pages/Usuarios/EditarUsuario.vue"),
          meta: {
            requiresAuth: true,
            breadcrumb: [
              { title: "Home", url: "/usuarios" },
              { title: "Usuários", url: "/usuarios" },
              { title: "Editar Usuário", active: true }
            ],
            pageTitle: "Editar Usuário"
          }
        },
        {
          path: "/usuarios/perfil",
          name: "perfil-usuario",
          component: () => import("./views/pages/Usuarios/PerfilUsuario.vue"),
          meta: {
            requiresAuth: true,
            breadcrumb: [
              { title: "Home", url: "/usuarios" },
              { title: "Usuários", url: "/usuarios" },
              { title: "Perfil Usuário", active: true }
            ],
            pageTitle: "Perfil Usuário"
          }
        },
        {
          path: "/usuarios/cadastrar",
          name: "cadastrar-usuario",
          component: () =>
            import("./views/pages/Usuarios/CadastrarUsuario.vue"),
          meta: {
            requiresAuth: true,
            breadcrumb: [
              { title: "Home", url: "/usuarios" },
              { title: "Usuários", url: "/usuarios" },
              { title: "Cadastrar Usuário", active: true }
            ],
            pageTitle: "Cadastrar Usuário"
          }
        },
        {
          path: "/usuarios/visualizar/:id",
          name: "visualizar-usuario",
          component: () =>
            import("./views/pages/Usuarios/VisualizarUsuario.vue"),
          meta: {
            requiresAuth: true,
            breadcrumb: [
              { title: "Home", url: "/usuarios" },
              { title: "Usuários", url: "/usuarios" },
              { title: "Visualizar Usuário", active: true }
            ],
            pageTitle: "Visualizar Usuário"
          }
        },
        {
          path: "/entidades",
          name: "entidades",
          meta: {
            requiresAuth: true,
            breadcrumb: [
              { title: "Home", url: "/entidades" },
              { title: "Entidades", url: "/entidades" },
              { title: "Editar Entidade", active: true }
            ],
            actions: [BotaoCadastrarEntidade]
          },
          component: () => import("./views/pages/Entidades/Index.vue")
        },
        {
          path: "/entidades/editar/:id",
          name: "gerenciar-entidade",
          component: () => import("./views/pages/Entidades/EditarEntidade.vue"),
          meta: {
            requiresAuth: true,
            breadcrumb: [
              { title: "Home", url: "/entidades" },
              { title: "Entidades", url: "/entidades" },
              { title: "Editar Entidade", active: true }
            ],
            pageTitle: "Editar Entidade"
          }
        },
        {
          path: "/entidades/cadastrar",
          name: "cadastrar-entidade",
          component: () =>
            import("./views/pages/Entidades/CadastrarEntidade.vue"),
          meta: {
            requiresAuth: true,
            breadcrumb: [
              { title: "Home", url: "/entidades" },
              { title: "Entidades", url: "/entidades" },
              { title: "Cadastrar Entidade", active: true }
            ],
            pageTitle: "Cadastrar Entidade"
          }
        },
        {
          path: "/parceiros",
          name: "parceiros",
          meta: {
            requiresAuth: true,
            breadcrumb: [
              { title: "Home", url: "/parceiros" },
              { title: "Parceiros", url: "/parceiros" },
              { title: "Editar Parceiro", active: true }
            ],
            actions: [BotaoCadastrarParceiro]
          },
          component: () => import("./views/pages/Parceiros/Index.vue")
        },
        {
          path: "/parceiros/editar/:id",
          name: "gerenciar-parceiro",
          component: () => import("./views/pages/Parceiros/EditarParceiro.vue"),
          meta: {
            requiresAuth: true,
            breadcrumb: [
              { title: "Home", url: "/parceiros" },
              { title: "Parceiros", url: "/parceiros" },
              { title: "Editar Parceiro", active: true }
            ],
            pageTitle: "Editar Parceiro"
          }
        },
        {
          path: "/parceiros/cadastrar",
          name: "cadastrar-parceiro",
          component: () =>
            import("./views/pages/Parceiros/CadastrarParceiro.vue"),
          meta: {
            requiresAuth: true,
            breadcrumb: [
              { title: "Home", url: "/parceiros" },
              { title: "Parceiros", url: "/parceiros" },
              { title: "Cadastrar Parceiro", active: true }
            ],
            pageTitle: "Cadastrar Parceiro"
          }
        },
        {
          path: "/relatorios/produtividade",
          name: "relatorios-produtividade",
          component: () => import("./views/pages/Relatorios/Produtividade.vue"),
          meta: {
            requiresAuth: true,
            breadcrumb: [
              { title: "Home" },
              { title: "Relatorios" },
              { title: "Produtividade", active: true }
            ],
            pageTitle: "Relatório de produtividade"
          }
        }
      ]
    },
    {
      path: "/analise-documentos/",
      name: "analisar-documentos",
      component: () =>
        import("./views/pages/AnaliseDocumentos/VisualizarDocumentos.vue"),
      meta: {
        requiresAuth: true,
        breadcrumb: [
          { title: "Home", url: "/contratos" },
          { title: "Contratos", url: "/contratos" },
          { title: "Analisar Documentos", active: true }
        ],
        pageTitle: "Analisar Documentos"
      }
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: "/login",
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        {
          path: "",
          name: "login",
          meta: {
            requiresAuth: false
          },
          component: () => import("@/views/pages/Login/Index.vue")
        },
        {
          path: "/esqueci-senha",
          name: "esqueci-senha",
          meta: {
            requiresAuth: false
          },
          component: () => import("@/views/pages/Login/EsqueciSenha.vue")
        },
        {
          path: "/alterar-senha/:token",
          name: "alterar-senha",
          meta: {
            requiresAuth: false
          },
          component: () => import("@/views/pages/Login/AlterarSenha.vue")
        },
        {
          path: "/autenticar/:codigo",
          name: "autenticar",
          meta: {
            requiresAuth: false
          },
          component: () => import("@/views/pages/Autenticar/Index.vue")
        },
        {
          path: "/contrato/imprimir/:id",
          name: "imprimir-contrato",
          meta: {
            requiresAuth: true,
            loading: true
          },
          component: () =>
            import("@/views/pages/Contratos/ImprimirContrato.vue")
        }
      ]
    },
    // Redirect to 404 page, if no match found
    {
      path: "*",
      name: "error-404",
      meta: {
        requiresAuth: false
      },
      component: () => import("@/views/pages/Error404.vue")
    }
  ]
});

router.beforeEach((to, from, next) => {
  let token = store.getters.getAccessToken();
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (token) {
      next();
    } else {
      next({
        path: "/login"
      });
    }
  } else {
    next();
  }
});

router.afterEach(to => {
  // Remove initial loading
  const appLoading = document.getElementById("loading-bg");
  if (appLoading && !to.matched.some(record => record.meta.loading)) {
    appLoading.style.display = "none";
  }
});

export default router;
